import { useEffect, useRef } from 'react'

import { useLocation, useNavigate } from 'react-router-dom'

import { FetchHelper } from '../../helpers/fetch'

import { useSimpleState } from '../../hooks/useSimpleState'

import './index.css'

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 250

const SearchComponent = ({ value, config={}, events={} }) => {
    const navigate = useNavigate()
    const location = useLocation()

    const debounceRef = useRef(null)

    const {_state} = useSimpleState({
        query: '',
        suggestions: [],
        highlightedIndex: -1,
        showSuggestions: false,
    })

    useEffect(() => {
        if (location.pathname !== '/search')
            return

        let params = new URLSearchParams(location.search)

        _state.set('query', params.get('q') || '')
    }, [location.pathname, location.search])

    const fetchSuggestions = (term) => {
        return FetchHelper({
            url: `/api/v1/deals/suggest?q=${encodeURIComponent(term)}`
        })
            .then((res) => {
                if (res.status === 200) {
                    _state.set('suggestions', res.body.data)
                    _state.set('highlightedIndex', -1)
                    _state.set('showSuggestions', true)
                }
            })
            .catch(() => {
                _state.set('suggestions', [])
            })
    }

    const handleChange = (event) => {
        let term = event.target.value

        _state.set('query', term)
        _state.set('highlightedIndex', -1)

        clearTimeout(debounceRef.current)

        if (term.trim().length < MIN_QUERY_LENGTH) {
            _state.set('suggestions', [])
            _state.set('showSuggestions', false)
            return
        }

        debounceRef.current = setTimeout(() => {
            fetchSuggestions(term.trim())
        }, DEBOUNCE_MS)
    }

    const goToDeal = (suggestion) => {
        _state.set('query', suggestion.name)
        _state.set('showSuggestions', false)
        _state.set('suggestions', [])
        navigate(`/deals/${suggestion.id}`)
    }

    const goToSearchResults = () => {
        let term = _state.get('query').trim()

        _state.set('showSuggestions', false)

        if (!term) {
            navigate('/')
            return
        }

        navigate(`/search?q=${encodeURIComponent(term)}`)
    }

    const handleKeyDown = (event) => {
        let suggestions = _state.get('suggestions')
        let highlightedIndex = _state.get('highlightedIndex')

        if (event.key === 'ArrowDown') {
            event.preventDefault()

            if (!_state.get('showSuggestions') || !suggestions.length)
                return

            _state.set('highlightedIndex', Math.min(highlightedIndex + 1, suggestions.length - 1))
        } else if (event.key === 'ArrowUp') {
            event.preventDefault()

            if (!_state.get('showSuggestions') || !suggestions.length)
                return

            _state.set('highlightedIndex', Math.max(highlightedIndex - 1, -1))
        } else if (event.key === 'Enter') {
            event.preventDefault()

            if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
                goToDeal(suggestions[highlightedIndex])
            } else {
                goToSearchResults()
            }
        } else if (event.key === 'Escape') {
            _state.set('showSuggestions', false)
        }
    }

    const handleBlur = () => {
        setTimeout(() => {
            _state.set('showSuggestions', false)
        }, 150)
    }

    const handleFocus = () => {
        if (_state.get('suggestions').length) {
            _state.set('showSuggestions', true)
        }
    }

    const getSuggestions = () => {
        if (!_state.get('showSuggestions') || !_state.get('suggestions').length)
            return null

        return (
            <ul className="search-suggestions">
                {_state.get('suggestions').map((item, index) => (
                    <li
                        key={`search-suggestion-${item.id}`}
                        className={`search-suggestionsItem ${index === _state.get('highlightedIndex') ? 'active' : ''}`}
                        onMouseDown={() => goToDeal(item)}
                    >
                        <div className="search-suggestionsItemImage" style={{ backgroundImage: `url(${item.imageUrl})` }}></div>
                        <div className="search-suggestionsItemDetails">
                            <div className="search-suggestionsItemName">{item.name}</div>
                            <div className="search-suggestionsItemAffiliate">{item.affiliateName}</div>
                        </div>
                        <div className="search-suggestionsItemPrice">${Number.parseFloat(item.priceFinal).toFixed(2)}</div>
                    </li>
                ))}
            </ul>
        )
    }

    return (
        <div className="c-Search">
            <div className="search-inputWrapper">
                <svg className="search-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="7" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>

                <input
                    type="text"
                    className="search-input"
                    placeholder="Search deals..."
                    value={_state.get('query')}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />
            </div>

            {getSuggestions()}
        </div>
    )
}

export { SearchComponent }
